import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsTracker from "./components/AnalyticsTracker";
import ErrorBoundary from "./components/ErrorBoundary";
import PublicLayout from "./components/PublicLayout";
import Hero from "./components/Hero";
import PressStrip from "./components/PressStrip";
import InstagramReelWall from "./components/InstagramReelWall";
import InstagramMarquee from "./components/InstagramMarquee";
import Story from "./components/Story";
import MenuHighlights from "./components/MenuHighlights";
import Gallery from "./components/Gallery";
import NewsletterSignup from "./components/NewsletterSignup";
import Contact from "./components/Contact";
import MenuPage from "./pages/MenuPage";
import MenuCategoryPage from "./pages/MenuCategoryPage";
import PrivateEventsPage from "./pages/PrivateEventsPage";
import StoryPage from "./pages/StoryPage";
import ChefsTablePage from "./pages/ChefsTablePage";
import CommunityPage from "./pages/CommunityPage";
import ContactPage from "./pages/ContactPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import DiwaliPage from "./pages/DiwaliPage";
import DiwaliBanner from "./components/DiwaliBanner";
import InstagramDesignPreview from "./pages/InstagramDesignPreview";
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import { Toaster } from "./components/ui/sonner";
import SEO from "./seo/SEO";
import { SITE, FAQ, DIWALI, DIWALI_FAQ, DIWALI_KEYWORDS, restaurantSchema, faqSchema, breadcrumbSchema, eventSchema, diwaliEventSchemas, diwaliPageSchema } from "./seo/config";

// Homepage body (Navbar/Footer come from PublicLayout)
const FullHome = () => {
  const [domainVerify, setDomainVerify] = React.useState('');
  React.useEffect(() => {
    import('./utils/api').then(({ contentAPI }) => {
      contentAPI.getMarketingConfig().then((cfg) => {
        if (cfg?.meta_domain_verification) setDomainVerify(cfg.meta_domain_verification);
      }).catch(() => {});
    });
  }, []);
  return (
    <>
      <SEO
        title={null /* default: Brick Lane — Los Angeles Soul. Indian Roots. */}
        description={`${SITE.short} Reservations at resy.com. Open Wed–Sun, 5pm–10:30pm.`}
        path="/"
        schemas={[restaurantSchema(), faqSchema(FAQ)]}
        extraTags={domainVerify ? [{ name: 'facebook-domain-verification', content: domainVerify }] : []}
      />
      <Hero />
      <PressStrip />
      <DiwaliBanner />
      <InstagramReelWall />
      <Story />
      <MenuHighlights />
      <Gallery />
      <InstagramMarquee />
      <NewsletterSignup />
      <Contact />
    </>
  );
};

const MenuRoute = () => (
  <>
    <SEO
      title="Menu — Modern Indian, Woodfire & Cocktails"
      description="Brick Lane's à la carte menu: chaat and small plates, tandoor and woodfire kebabs, regional classics reimagined, biryani, fresh tandoor breads, and craft cocktails inspired by Indian spices."
      path="/menu"
      schemas={[
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Menu", path: "/menu" },
        ]),
      ]}
    />
    <MenuPage />
  </>
);

const ChefsTableRoute = () => (
  <>
    <SEO
      title="Chef's Table — 7-Course Indian Tasting Menu in LA"
      description="An intimate 7-course Indian tasting menu by Chef Sanjay Rawat in the LA Arts District. Curated wine & cocktail pairings. 2–12 guests, Wed–Sun, 6:00pm & 8:30pm seatings."
      path="/chefs-table"
      schemas={[
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Chef's Table", path: "/chefs-table" },
        ]),
        eventSchema({
          name: "Brick Lane Chef's Table",
          description:
            "7-course Indian tasting menu with wine and cocktail pairings, behind-the-scenes kitchen tour, and personalized chef interaction.",
          url: `${SITE.url}/chefs-table`,
        }),
      ]}
    />
    <ChefsTablePage />
  </>
);

const CommunityRoute = () => (
  <>
    <SEO
      title="Community — Impact LA Food Donor Partner"
      description="Brick Lane is a proud food donor partner of Impact LA, redistributing surplus wholesome meals to underserved children, families, and shelters across Los Angeles County."
      path="/community"
      schemas={[
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Community", path: "/community" },
        ]),
      ]}
    />
    <CommunityPage />
  </>
);

const PrivateEventsRoute = () => (
  <>
    <SEO
      title="Private Events & Catering — Buyouts, Private Dining, Off-Site"
      description="Host private events at Brick Lane: full restaurant buyouts (up to 350 guests), semi-private dining (12–125 guests), and off-site catering. Indian-inspired tasting menus, dedicated event team."
      path="/private-events"
      schemas={[
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Private Events", path: "/private-events" },
        ]),
      ]}
    />
    <PrivateEventsPage />
  </>
);

const OurStoryRoute = () => (
  <>
    <SEO
      title="Our Story — Chef Sanjay Rawat & Co-Founders"
      description="Brick Lane is the vision of Chef Sanjay Rawat (formerly Kahani, The Ritz-Carlton Laguna Niguel) with co-founders Rishma Shariff and Sachin Nayyar — reimagining regional Indian cuisine through a California lens."
      path="/our-story"
      schemas={[
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Our Story", path: "/our-story" },
        ]),
      ]}
    />
    <StoryPage />
  </>
);

const DiwaliRoute = () => (
  <>
    <SEO
      title="Diwali Gala 2026 in Los Angeles — Nov 6 & 7, Arts District"
      description="Celebrate Diwali 2026 at Brick Lane in the LA Arts District on Friday, November 6 and Saturday, November 7. Live chaat stations, family-style grand feast, curated open bar, live DJ and complimentary henna. $95 per person, plus tax, gratuity and beverages. Book on Resy."
      path="/diwali"
      image={DIWALI.poster}
      type="article"
      keywords={DIWALI_KEYWORDS}
      schemas={[
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Diwali Gala 2026", path: "/diwali" },
        ]),
        diwaliPageSchema(),
        ...diwaliEventSchemas().map((e) => ({ "@context": "https://schema.org", ...e })),
        faqSchema(DIWALI_FAQ),
      ]}
    />
    <DiwaliPage />
  </>
);

const ContactRoute = () => (
  <>
    <SEO
      title="Contact & Reservations — 1331 E 6th St, Arts District"
      description="Visit Brick Lane at 1331 E 6th St, Arts District, Los Angeles. Reservations on Resy or call (213) 395-0110. Open Wed–Sun, 5:00pm–10:30pm."
      path="/contact"
      schemas={[
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]),
      ]}
    />
    <ContactPage />
  </>
);

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <Routes>
            {/* Public routes share a persistent Navbar + Footer */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<FullHome />} />
              <Route path="/menu" element={<MenuRoute />} />
              <Route path="/menu/:category" element={<MenuCategoryPage />} />
              <Route path="/diwali" element={<DiwaliRoute />} />
              <Route path="/private-events" element={<PrivateEventsRoute />} />
              <Route path="/our-story" element={<OurStoryRoute />} />
              <Route path="/chefs-table" element={<ChefsTableRoute />} />
              <Route path="/community" element={<CommunityRoute />} />
              <Route path="/contact" element={<ContactRoute />} />
            </Route>

            {/* Standalone routes (no global chrome) */}
            <Route path="/coming-soon" element={<ComingSoonPage />} />
            <Route path="/design/instagram" element={<InstagramDesignPreview />} />
            <Route path="/new" element={<Navigate to="/" replace />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </ErrorBoundary>
  );
}

export default App;
